import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase, MapPin, Clock, DollarSign, Send, Loader2, CheckCircle,
  ArrowLeft, Building2, Calendar, Share2, Users, Linkedin, Twitter,
  Facebook, Copy, Mail, UserPlus
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom, invokeFunction } from '@/integrations/pms';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { SEOHead } from '@/components/seo/SEOHead';
import { BreadcrumbJsonLd } from '@/components/seo/JsonLd';
import { useSiteContent } from '@/context/SiteContentContext';
import NotFound from '@/pages/NotFound';

interface Job {
  id: string;
  title: string;
  department: string | null;
  location: string | null;
  employment_type: string | null;
  salary_range: string | null;
  description: string | null;
  requirements: string | null;
  benefits: string | null;
  published_at: string | null;
  created_at: string;
}

const slugify = (text: string) =>
  text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

export default function JobDetailPage() {
  const { jobSlug } = useParams<{ jobSlug: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();
  const siteContent = useSiteContent();
  const companyName = siteContent.template?.brand?.name || 'Our Company';
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const { data: job, isLoading } = useQuery({
    queryKey: ['public-job', jobSlug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('jobs')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });
      if (error) throw error;
      const matched = (data as Job[]).find(j => slugify(j.title) === jobSlug);
      return matched || null;
    },
    enabled: !!jobSlug,
  });

  const { data: otherJobs = [] } = useQuery({
    queryKey: ['public-jobs-sidebar'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('jobs')
        .select('id, title, department, location, employment_type')
        .eq('is_published', true)
        .order('published_at', { ascending: false });
      if (error) throw error;
      return data as Pick<Job, 'id' | 'title' | 'department' | 'location' | 'employment_type'>[];
    },
  });

  const handleApply = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!job) return;
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);

    try {
      const applicationData = {
        job_id: job.id,
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        phone: formData.get('phone') as string || null,
        linkedin_url: formData.get('linkedin') as string || null,
        portfolio_url: formData.get('portfolio') as string || null,
        years_experience: parseInt(formData.get('experience') as string) || null,
        cover_letter: formData.get('coverLetter') as string || null,
      };

      const { data: insertedApp, error } = await pmsFrom('job_applications').insert(applicationData).select('id').single();
      if (error) throw error;

      // Create a lead record for the application, then notify
      try {
        const { data: insertedLead } = await pmsFrom('leads').insert({
          name: applicationData.name,
          email: applicationData.email,
          phone: applicationData.phone || null,
          service_needed: `Job Application: ${job.title}`,
          message: `New application for "${job.title}"${applicationData.cover_letter ? `\n\nCover Letter:\n${applicationData.cover_letter}` : ''}${applicationData.linkedin_url ? `\nLinkedIn: ${applicationData.linkedin_url}` : ''}${applicationData.portfolio_url ? `\nPortfolio: ${applicationData.portfolio_url}` : ''}`,
          source: 'careers',
          customer_type: 'new',
        }).select('id').single();

        if (insertedLead?.id) {
          await invokeFunction('notify-lead', {
            body: {
              lead_id: insertedLead.id,
              name: applicationData.name,
              email: applicationData.email,
              phone: applicationData.phone || '',
              service_needed: `Job Application: ${job.title}`,
              message: `New application for "${job.title}"${applicationData.cover_letter ? `\n\nCover Letter:\n${applicationData.cover_letter}` : ''}${applicationData.linkedin_url ? `\nLinkedIn: ${applicationData.linkedin_url}` : ''}${applicationData.portfolio_url ? `\nPortfolio: ${applicationData.portfolio_url}` : ''}`,
              source: 'careers',
            },
          });
        }
      } catch (notifyErr) {
        console.warn('Notification email failed (non-blocking):', notifyErr);
      }

      setIsSuccess(true);
      toast({ title: 'Application Submitted!', description: "We'll review your application and get back to you soon." });
    } catch (error) {
      console.error('Application error:', error);
      toast({ title: 'Submission Failed', description: 'Please try again or contact us directly.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = job?.title ? `${job.title} at ${companyName}` : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    toast({ title: 'Link copied to clipboard' });
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`Check out this job: ${shareTitle}`);
    const body = encodeURIComponent(`I found this job posting that might interest you:\n\n${shareTitle}\n${currentUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleReferral = () => {
    const subject = encodeURIComponent(`Job Referral: ${shareTitle}`);
    const body = encodeURIComponent(`I'd like to refer someone for this position:\n\n${shareTitle}\n${currentUrl}\n\nCandidate Name: \nCandidate Email: \nCandidate Phone: `);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!job) return <NotFound />;

  const seoTitle = `${job.title}${job.location ? ` in ${job.location}` : ''} | Careers at ${companyName}`;
  const seoDescription = job.description?.slice(0, 155) || `Apply for the ${job.title} position at ${companyName}. ${job.employment_type || 'Full-time'} role${job.location ? ` in ${job.location}` : ''}.`;
  const canonicalUrl = `${baseUrl}/careers/${slugify(job.title)}`;
  const filteredOtherJobs = otherJobs.filter(j => j.id !== job.id);

  const renderContent = (text: string | null) => {
    if (!text) return null;
    const lines = text.split(/\n|\r\n/).filter(Boolean);
    const bullets = lines.filter(l => l.trim().startsWith('•') || l.trim().startsWith('-'));
    const paragraphs = lines.filter(l => !l.trim().startsWith('•') && !l.trim().startsWith('-'));

    return (
      <div className="space-y-2">
        {paragraphs.length > 0 && (
          <p className="text-muted-foreground leading-relaxed">{paragraphs.join(' ')}</p>
        )}
        {bullets.length > 0 && (
          <ul className="space-y-1.5 mt-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>{b.replace(/^[•\-]\s*/, '')}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const jobPostingSchema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description || '',
    datePosted: job.published_at || job.created_at,
    employmentType: job.employment_type?.toUpperCase().replace(/-/g, '_') || 'FULL_TIME',
    hiringOrganization: {
      "@type": "Organization",
      name: companyName,
      sameAs: baseUrl,
    },
    jobLocation: job.location ? {
      "@type": "Place",
      address: { "@type": "PostalAddress", addressLocality: job.location },
    } : undefined,
    baseSalary: job.salary_range ? {
      "@type": "MonetaryAmount",
      currency: "USD",
      value: { "@type": "QuantitativeValue", value: job.salary_range, unitText: "YEAR" },
    } : undefined,
  };

  return (
    <>
      <SEOHead title={seoTitle} description={seoDescription} canonical={canonicalUrl} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPostingSchema) }} />
      <BreadcrumbJsonLd items={[
        { name: 'Home', url: baseUrl },
        { name: 'Careers', url: `${baseUrl}/careers` },
        { name: job.title, url: canonicalUrl },
      ]} />

      <div className="min-h-screen">
        {/* Header */}
        <section className="bg-muted/20 border-b border-border">
          <div className="container mx-auto px-4 py-10 lg:py-14">
            <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
              <ArrowLeft className="h-4 w-4" />
              Back to All Positions
            </Link>

            <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mb-3">{job.title}</h1>
            
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {job.department && (
                <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
                  <Building2 className="h-4 w-4" />{job.department}
                </span>
              )}
              {job.location && (
                <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
                  <MapPin className="h-4 w-4" />{job.location}
                </span>
              )}
              {job.employment_type && (
                <Badge variant="secondary">{job.employment_type}</Badge>
              )}
            </div>

            {job.salary_range && (
              <div className="flex items-center gap-1.5 text-lg font-semibold text-primary">
                <DollarSign className="h-5 w-5" />
                {job.salary_range}
                <span className="text-sm font-normal text-muted-foreground">(per year)</span>
              </div>
            )}
          </div>
        </section>

        {/* Main Content — 2/3 + 1/3 Layout */}
        <section className="py-10 lg:py-14">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
              {/* Left — Job Details */}
              <article className="lg:col-span-2 space-y-8">
                {job.description && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <Card>
                      <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-4">About This Role</h2>
                        {renderContent(job.description)}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {job.requirements && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Card>
                      <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-4">Requirements</h2>
                        {renderContent(job.requirements)}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {job.benefits && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <Card>
                      <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-4">Benefits</h2>
                        {renderContent(job.benefits)}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Application Form (expanded) */}
                {showForm && (
                  <motion.div
                    id="apply"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="border-primary/20">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Send className="h-5 w-5 text-primary" />
                          Apply for {job.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {isSuccess ? (
                          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-8 text-center">
                            <CheckCircle className="h-14 w-14 text-primary mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">Application Received!</h3>
                            <p className="text-sm text-muted-foreground">We'll review your application and contact you soon.</p>
                          </motion.div>
                        ) : (
                          <form onSubmit={handleApply} className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="name">Full Name *</Label>
                              <Input id="name" name="name" required placeholder="John Smith" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="email">Email *</Label>
                              <Input id="email" name="email" type="email" required placeholder="john@example.com" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="phone">Phone</Label>
                              <Input id="phone" name="phone" type="tel" placeholder="(555) 123-4567" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="experience">Years of Experience</Label>
                              <Select name="experience">
                                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="0">0-1 years</SelectItem>
                                  <SelectItem value="2">2-3 years</SelectItem>
                                  <SelectItem value="4">4-5 years</SelectItem>
                                  <SelectItem value="6">6-10 years</SelectItem>
                                  <SelectItem value="10">10+ years</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="linkedin">LinkedIn Profile</Label>
                              <Input id="linkedin" name="linkedin" type="url" placeholder="https://linkedin.com/in/..." />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="portfolio">Portfolio / Website</Label>
                              <Input id="portfolio" name="portfolio" type="url" placeholder="https://..." />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                              <Label htmlFor="coverLetter">Why are you interested?</Label>
                              <Textarea id="coverLetter" name="coverLetter" rows={4} placeholder="Tell us about yourself and why you'd be a great fit..." />
                            </div>
                            <div className="sm:col-span-2">
                              <Button type="submit" className="w-full" disabled={isSubmitting}>
                                {isSubmitting ? (
                                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</>
                                ) : (
                                  <><Send className="h-4 w-4 mr-2" /> Submit Application</>
                                )}
                              </Button>
                            </div>
                          </form>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </article>

              {/* Right Sidebar */}
              <aside className="space-y-6">
                {/* Apply Now CTA */}
                <Card className="border-primary/20 sticky top-24">
                  {siteContent.template?.brand?.logo && (
                    <div className="flex justify-center p-6 pb-0">
                      <img
                        src={siteContent.template.brand.logo}
                        alt={`${companyName} logo`}
                        className="h-16 w-auto object-contain"
                      />
                    </div>
                  )}
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-lg font-bold">Apply Now</h3>
                    <p className="text-sm text-muted-foreground">Submit your application for this position</p>
                    <p className="text-sm text-muted-foreground">
                      Ready to join {companyName}? Click below to start your application.
                    </p>
                    <Button
                      className="w-full gap-2"
                      onClick={() => {
                        setShowForm(true);
                        setTimeout(() => {
                          document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                    >
                      Start Application <ArrowLeft className="h-4 w-4 rotate-180" />
                    </Button>
                  </CardContent>
                </Card>

                {/* Share This Position */}
                <Card>
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                      <Share2 className="h-5 w-5" /> Share This Position
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 text-xs rounded-md border border-input bg-background px-3 h-9 hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        <Linkedin className="h-4 w-4" /> LinkedIn
                      </a>
                      <a
                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(currentUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 text-xs rounded-md border border-input bg-background px-3 h-9 hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        <Twitter className="h-4 w-4" /> Twitter
                      </a>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 text-xs rounded-md border border-input bg-background px-3 h-9 hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        <Facebook className="h-4 w-4" /> Facebook
                      </a>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleCopyLink}>
                        <Copy className="h-4 w-4" /> Copy Link
                      </Button>
                      <a
                        href={`mailto:?subject=${encodeURIComponent(`Check out this job: ${shareTitle}`)}&body=${encodeURIComponent(`I found this job posting that might interest you:\n\n${shareTitle}\n${currentUrl}`)}`}
                        className="inline-flex items-center justify-center gap-1.5 text-xs rounded-md border border-input bg-background px-3 h-9 hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        <Mail className="h-4 w-4" /> Email
                      </a>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <UserPlus className="h-4 w-4" />
                        Know someone perfect for this role? Refer them and help us find great talent!
                      </p>
                      <a
                        href={`mailto:?subject=${encodeURIComponent(`Job Referral: ${shareTitle}`)}&body=${encodeURIComponent(`I'd like to refer someone for this position:\n\n${shareTitle}\n${currentUrl}\n\nCandidate Name: \nCandidate Email: \nCandidate Phone: `)}`}
                        className="inline-flex items-center justify-center gap-2 w-full rounded-md border border-input bg-background px-4 h-10 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        <UserPlus className="h-4 w-4" /> Refer a Friend
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Other Open Positions */}
                {filteredOtherJobs.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        Other Open Positions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {filteredOtherJobs.slice(0, 5).map(j => (
                        <Link
                          key={j.id}
                          to={`/careers/${slugify(j.title)}`}
                          className="block p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <p className="font-medium text-sm group-hover:text-primary transition-colors">{j.title}</p>
                          <div className="flex gap-2 mt-1">
                            {j.department && <span className="text-xs text-muted-foreground">{j.department}</span>}
                            {j.location && <span className="text-xs text-muted-foreground">• {j.location}</span>}
                          </div>
                        </Link>
                      ))}
                      {filteredOtherJobs.length > 5 && (
                        <Link to="/careers" className="block text-center text-xs text-primary hover:underline pt-2">
                          View all {filteredOtherJobs.length} positions →
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                )}
              </aside>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
